import React, { useState, useEffect } from "react";
import {
  Bot, Check, X, Wand2, Zap, CheckCircle2, RefreshCw, Flag,
  Loader2, Sparkles, Image, MessageSquareWarning, RotateCcw, Heart,
  ShieldAlert, CreditCard,
} from "lucide-react";

// ─── Steps for thinking animation ────────────────────────────────────────────
const STEPS = [
  { text: "Leyendo el código de tu sitio web...",       est: 2  },
  { text: "Identificando elementos editables...",        est: 4  },
  { text: "Planificando los cambios con IA...",          est: 8  },
  { text: "Preparando la propuesta...",                  est: 12 },
];

// ─── Bot Avatar ───────────────────────────────────────────────────────────────
export function BotAvatar() {
  return (
    <div className="w-8 h-8 rounded-full bg-[#0A0A0A] flex items-center justify-center flex-shrink-0 mt-0.5">
      <Bot size={15} className="text-[#ffc700]" />
    </div>
  );
}

// ─── Thinking Bubble ──────────────────────────────────────────────────────────
export function ThinkingBubble({ stage }) {
  const cur = stage ?? 0;
  const TOTAL_EST = STEPS[cur].est;
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    setElapsed(0);
    const t = setInterval(() => setElapsed(s => s + 1), 1000);
    return () => clearInterval(t);
  }, []); // eslint-disable-line

  const estLeft = Math.max(0, TOTAL_EST - elapsed);
  const pct = Math.min(98, Math.round((elapsed / Math.max(TOTAL_EST, 1)) * 100));

  return (
    <>
      <style>{`
        @keyframes stepIn { from { opacity:0; transform:translateY(5px); } to { opacity:1; transform:translateY(0); } }
        .step-in { animation: stepIn 0.3s ease forwards; }
      `}</style>
      <div className="flex items-start gap-2.5">
        <BotAvatar />
        <div className="bg-white border border-gray-200 rounded-2xl rounded-tl-sm px-4 py-3.5 max-w-[85%] min-w-[240px]">
          <div className="space-y-2.5 mb-3">
            {STEPS.slice(0, cur + 1).map((step, i) => {
              const done = i < cur;
              const active = i === cur;
              return (
                <div key={step.label || i} className={active ? "step-in flex items-center gap-2.5" : "flex items-center gap-2.5"}>
                  {done ? (
                    <span className="w-4 h-4 rounded-full bg-green-50 border border-green-200 flex items-center justify-center flex-shrink-0">
                      <Check size={9} className="text-green-500" />
                    </span>
                  ) : (
                    <span className="flex gap-0.5 flex-shrink-0">
                      {[0,1,2].map(d => (
                        <span key={d} className="w-1.5 h-1.5 bg-[#6D28D9] rounded-full animate-bounce"
                          style={{ animationDelay: `${d*0.15}s` }} />
                      ))}
                    </span>
                  )}
                  <span className={`text-xs leading-snug ${done ? "text-gray-400 line-through" : "text-gray-700 font-semibold"}`}>
                    {step.text}
                  </span>
                </div>
              );
            })}
          </div>
          <div className="border-t border-gray-100 pt-2.5">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">Tiempo estimado</span>
              <span className="text-[11px] font-bold text-[#6D28D9] tabular-nums">
                {estLeft > 0 ? `~${estLeft} seg` : "finalizando..."}
              </span>
            </div>
            <div className="w-full h-1 bg-gray-100 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-[#6D28D9] to-[#a855f7] rounded-full transition-all duration-1000"
                style={{ width: `${Math.max(3, pct)}%` }} />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

// ─── Helper row ───────────────────────────────────────────────────────────────
function Row({ label, value, bold, accent }) {
  return (
    <div className="flex justify-between items-center">
      <span className="text-xs text-gray-500">{label}</span>
      <span className={`text-xs ${bold ? "font-black" : "font-medium"} ${accent ? "text-amber-600" : "text-gray-800"}`}>{value}</span>
    </div>
  );
}

// ─── Pricing Proposal Bubble ──────────────────────────────────────────────────
export function PricingProposalBubble({ msg, onAccept, onDecline, loading }) {
  const isAccepted = msg.status === "accepted";
  const isDeclined = msg.status === "declined";

  if (isAccepted || isDeclined) {
    return (
      <div className="flex justify-center py-1">
        <div className={`flex items-center gap-1.5 text-[11px] px-3 py-1.5 rounded-full border ${isAccepted ? "bg-green-50 border-green-200 text-green-700" : "bg-gray-100 border-gray-200 text-gray-500"}`}>
          {isAccepted ? <Check size={10} /> : <X size={10} />}
          {isAccepted ? "Presupuesto aceptado — cambios en proceso" : "Presupuesto rechazado"}
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-start gap-2.5">
      <BotAvatar />
      <div className="max-w-[90%] space-y-2">
        <div className="bg-amber-50 border border-amber-200 rounded-2xl rounded-tl-sm p-4">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles size={14} className="text-amber-600" />
            <span className="text-sm font-black text-amber-800">Modificación significativa detectada</span>
          </div>
          <p className="text-xs text-gray-600 mb-3">Esta modificación agrega contenido nuevo al sitio. Implica un ajuste en tu plan mensual.</p>
          <div className="space-y-1.5 bg-white rounded-xl p-3 mb-3">
            <Row label="Plan actual" value={`$${msg.current_price?.toFixed(2)} USD/mes`} />
            <Row label="Líneas actuales" value={`~${msg.current_lines?.toLocaleString()}`} />
            <Row label="Líneas a agregar" value={`~${msg.estimated_new_lines}`} />
            <div className="border-t border-gray-100 pt-1.5 mt-1.5">
              <Row label="Ajuste mensual" value={`+$${msg.additional_price?.toFixed(2)} USD/mes`} bold accent />
              <Row label="Nuevo total" value={`$${msg.new_total?.toFixed(2)} USD/mes`} bold />
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={() => onAccept(msg)} disabled={loading} data-testid="accept-pricing-btn"
              className="flex-1 py-2.5 bg-[#0A0A0A] text-white rounded-xl text-sm font-bold hover:bg-gray-800 flex items-center justify-center gap-1.5 disabled:opacity-60">
              {loading ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
              Aceptar presupuesto
            </button>
            <button onClick={() => onDecline(msg)} disabled={loading}
              className="px-4 py-2.5 bg-gray-100 text-gray-600 rounded-xl text-sm font-semibold hover:bg-gray-200">
              No, cancelar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Agent Bubble ─────────────────────────────────────────────────────────────
export function AgentBubble({ msg, onAccept, onContinue, onConfirm, onRestore, onRestoreFlow, accepting, acceptingTimer, restoring, prevInstruction, previewLoading, onDidntWork, onLoved, onApprovePlan, onRejectPlan }) {
  if (msg.type === "pricing_proposal") return null;

  // Iter91.3 — Big-change quote bubble. Agent ran out of turns; client must
  // pay a fixed quote to unlock 30 turns and finish the work.
  if (msg.type === "agent_v2_big_change") {
    const q = msg.quote || {};
    const checkoutUrl = q.checkout_url;
    const paid = q.payment_status === "paid";
    return (
      <div className="flex items-start gap-2.5" data-testid="agent-v2-big-change-bubble">
        <BotAvatar />
        <div className="bg-amber-50 border border-amber-300 rounded-2xl rounded-tl-sm px-4 py-3 max-w-[92%] w-full">
          <div className="flex items-center gap-2 mb-2">
            <ShieldAlert size={13} className="text-amber-700" />
            <span className="text-[11px] font-black uppercase tracking-wide text-amber-900">
              Modificación grande · presupuesto
            </span>
          </div>
          <p className="text-[12.5px] text-gray-800 leading-relaxed mb-2">
            {msg.content}
          </p>
          <div className="bg-white border border-amber-200 rounded-xl px-3 py-2.5 mb-2.5">
            <p className="text-[10px] font-black text-gray-500 uppercase tracking-wider mb-0.5">Pedido</p>
            <p className="text-[12px] text-gray-800 italic mb-2">"{q.instruction || msg.instruction || ''}"</p>
            <div className="flex items-center justify-between">
              <span className="text-[11px] text-gray-600">
                <span className="font-bold">{q.attempted_steps ?? 0} pasos</span>
                <span className="text-gray-400"> ({q.applied_steps ?? 0} aplicados, {q.inspected_steps ?? 0} inspecciones)</span>
              </span>
              <span className="text-[18px] font-black text-amber-700">
                ${typeof q.amount_usd === 'number' ? q.amount_usd.toFixed(2) : q.amount_usd} USD
              </span>
            </div>
          </div>
          <p className="text-[11px] text-gray-600 leading-relaxed mb-3">
            Los <b>créditos IA</b> son para cambios sencillos (texto, color, tamaño).
            Los cambios estructurales (reordenar secciones, agregar funcionalidad) se cobran aparte.
            Al pagar, el agente continúa con <b>30 pasos</b> en vez de 15.
          </p>
          {paid ? (
            <span className="inline-block text-[11.5px] font-bold text-emerald-700">
              ✓ Pagado · esperando que el agente continúe…
            </span>
          ) : checkoutUrl ? (
            <a
              href={checkoutUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="big-change-pay-btn"
              className="flex items-center justify-center gap-1.5 py-2.5 bg-amber-500 text-amber-950 rounded-lg text-[12.5px] font-black hover:bg-amber-400 active:scale-95 transition-all w-full"
            >
              <CreditCard size={13} strokeWidth={2.5} />
              Pagar ${typeof q.amount_usd === 'number' ? q.amount_usd.toFixed(2) : q.amount_usd} y aplicar
            </a>
          ) : (
            <p className="text-[11px] text-red-700 font-bold italic">
              ⚠ No pude generar el link de pago. Reintentá el pedido o contactanos.
            </p>
          )}
        </div>
      </div>
    );
  }

  // Iter88 — Plan-mode bubble. Agent proposed steps; user approves or
  // rejects. NO HTML edits applied yet at this point.
  if (msg.type === "agent_v2_plan") {
    const decision = msg.plan_decision;
    const plan = msg.plan || {};
    const steps = Array.isArray(plan.steps) ? plan.steps : [];
    return (
      <div className="flex items-start gap-2.5" data-testid="agent-v2-plan-bubble">
        <BotAvatar />
        <div className="bg-purple-50 border border-purple-200 rounded-2xl rounded-tl-sm px-4 py-3 max-w-[92%] w-full">
          <div className="flex items-center gap-2 mb-2">
            <Wand2 size={13} className="text-purple-600" />
            <span className="text-[11px] font-black uppercase tracking-wide text-purple-800">
              Plan propuesto por el agente
            </span>
          </div>
          {plan.title && (
            <h4 className="text-[13.5px] font-bold text-gray-900 mb-1">{plan.title}</h4>
          )}
          {plan.summary && (
            <p className="text-[12.5px] text-gray-700 leading-relaxed mb-2">{plan.summary}</p>
          )}
          {steps.length > 0 && (
            <ol className="text-[12px] text-gray-800 space-y-1 mb-2 ml-1">
              {steps.map((s, i) => (
                <li key={i} className="flex gap-2">
                  <span className="font-bold text-purple-700 min-w-[16px]">{i + 1}.</span>
                  <span className="flex-1">{s}</span>
                </li>
              ))}
            </ol>
          )}
          {plan.scope && (
            <p className="text-[10.5px] text-gray-500 italic mb-2">
              <span className="font-bold not-italic text-gray-600">Alcance:</span> {plan.scope}
            </p>
          )}
          {!decision && onApprovePlan && (
            <div className="flex flex-col sm:flex-row gap-2 mt-2">
              <button
                onClick={() => onApprovePlan(msg)}
                data-testid="approve-plan-btn"
                className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-[#6D28D9] text-white rounded-lg text-[12px] font-bold hover:bg-purple-800 active:scale-95 transition-all"
              >
                <Check size={13} strokeWidth={2.5} />
                Aprobar y aplicar (1 crédito)
              </button>
              <button
                onClick={() => onRejectPlan?.(msg)}
                data-testid="reject-plan-btn"
                className="flex items-center justify-center gap-1.5 py-2 px-3 bg-gray-100 text-gray-700 rounded-lg text-[12px] font-semibold hover:bg-gray-200 active:scale-95 transition-all"
              >
                <X size={13} /> Rechazar
              </button>
            </div>
          )}
          {decision === "approved" && (
            <span className="inline-block mt-1 text-[10.5px] font-bold text-emerald-700 italic">
              ✓ Aprobado · ejecutando…
            </span>
          )}
          {decision === "rejected" && (
            <span className="inline-block mt-1 text-[10.5px] font-bold text-gray-500 italic">
              Rechazado
            </span>
          )}
        </div>
      </div>
    );
  }

  // Iter86/88 — Agent v2 result bubble. Shows the summary + a compact
  // tool log in plain Spanish. NO COSTS exposed to the client (those go
  // to the admin dashboard).
  if (msg.type === "agent_v2_done") {
    const isError = !!msg.error;
    const accentBg = isError ? "bg-amber-50 border-amber-200" : "bg-emerald-50 border-emerald-200";
    const accentText = isError ? "text-amber-800" : "text-emerald-800";
    // Spanish labels for each action type — replaces the raw English
    // tool names so non-technical users understand what happened.
    const ACTION_LABEL = {
      view_section:      "Inspeccioné una sección del sitio",
      find_replace:      "Hice un cambio puntual",
      find_replace_many: "Apliqué un grupo de cambios",
      done:              "Terminé y guardé los cambios",
      parse_error:       "Reintenté el paso anterior",
      propose_plan:      "Armé el plan",
      unknown:           "Acción desconocida",
    };
    return (
      <div className="flex items-start gap-2.5" data-testid="agent-v2-bubble">
        <BotAvatar />
        <div className={`${accentBg} border rounded-2xl rounded-tl-sm px-4 py-3 max-w-[92%]`}>
          <div className="flex items-center gap-2 mb-1.5">
            <Sparkles size={13} className={isError ? "text-amber-600" : "text-emerald-600"} />
            <span className={`text-[11px] font-black uppercase tracking-wide ${accentText}`}>
              {isError ? "Aviso" : msg.applied ? "Cambios aplicados" : "Listo"}
            </span>
            {msg.credits_billed > 0 && !isError && (
              <span className="ml-auto text-[10px] font-bold text-emerald-700 bg-white/70 px-1.5 py-0.5 rounded">
                {msg.credits_billed} créd.
              </span>
            )}
          </div>
          <p className="text-[13px] text-gray-800 leading-relaxed whitespace-pre-line">{msg.content}</p>
          {Array.isArray(msg.actions) && msg.actions.length > 0 && (
            <details className="mt-2">
              <summary className="text-[10.5px] font-bold text-gray-600 cursor-pointer hover:text-gray-900 select-none">
                Ver lo que hizo el agente ({msg.actions.length} {msg.actions.length === 1 ? "paso" : "pasos"})
              </summary>
              <ol className="mt-1.5 space-y-1.5 text-[11px] pl-4">
                {msg.actions.map((a, i) => {
                  const label = ACTION_LABEL[a.action] || a.action || "Paso";
                  const failed = a.result && a.result.error;
                  // Iter91.2 — show the selector OR line range so the user can
                  // see where the agent looked, instead of 8x "Inspeccioné".
                  const locator = a.args?.selector
                    || (a.args?.start_line ? `líneas ${a.args.start_line}–${a.args.end_line || "?"}` : "");
                  return (
                    <li key={i} className={`flex items-start gap-1.5 ${failed ? "text-amber-700" : "text-gray-700"}`}>
                      <span className={`font-bold mt-0.5 ${failed ? "text-amber-500" : "text-emerald-500"}`}>{failed ? "⚠" : "✓"}</span>
                      <span className="flex-1 break-words">
                        {label}
                        {a.count ? ` (${a.count} reemplazos)` : ""}
                        {locator ? <span className="text-gray-400 italic"> · {locator}</span> : null}
                        {failed ? <span className="text-amber-600 block"> {a.result.error}</span> : null}
                      </span>
                    </li>
                  );
                })}
              </ol>
            </details>
          )}
          {(onDidntWork || onLoved) && msg.applied && !msg.retried && !msg.loved_text && msg.instruction && (
            <div className="mt-2 flex flex-wrap gap-2">
              {onLoved && (
                <button
                  onClick={() => onLoved(msg)}
                  data-testid="loved-btn-v2"
                  className="inline-flex items-center gap-1.5 text-[11px] font-bold text-pink-800 bg-pink-100 hover:bg-pink-200 border border-pink-300 px-2.5 py-1 rounded-lg transition-colors"
                >
                  <Heart size={11} strokeWidth={2.5} />
                  Me encantó
                </button>
              )}
              {onDidntWork && (
                <button
                  onClick={() => onDidntWork(msg)}
                  data-testid="didnt-work-btn-v2"
                  className="inline-flex items-center gap-1.5 text-[11px] font-bold text-amber-800 bg-amber-100 hover:bg-amber-200 border border-amber-300 px-2.5 py-1 rounded-lg transition-colors"
                >
                  <RotateCcw size={11} strokeWidth={2.5} />
                  No funcionó
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  // AST fast-path success — single-step structured edit applied directly.
  // Renders as a green confirmation tag so the user knows there's nothing to
  // accept/reject (it's already live). Includes a "No funcionó" escape hatch
  // that undoes the change and re-prompts the AI with failure context
  // (ported from grapesjs-ai-copilot's "Didn't work" pattern), and a
  // "Me encantó" memory hook that saves the instruction as a style
  // preference for future AI calls.
  if (msg.type === "ast_ops_applied") {
    return (
      <div className="flex items-start gap-2.5" data-testid="ast-ops-applied-bubble">
        <BotAvatar />
        <div className="bg-emerald-50 border border-emerald-200 rounded-2xl rounded-tl-sm px-4 py-3 max-w-[90%]">
          <div className="flex items-center gap-2 mb-1.5">
            <CheckCircle2 size={14} className="text-emerald-600" />
            <span className="text-[11px] font-black text-emerald-800 uppercase tracking-wide">
              Cambio aplicado
            </span>
            {msg.ops_count > 0 && (
              <span className="ml-auto text-[10px] font-bold text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded">
                {msg.ops_count} op{msg.ops_count === 1 ? "" : "s"}
              </span>
            )}
          </div>
          <p className="text-[13px] text-gray-800 leading-relaxed">{msg.content}</p>
          {msg.loved_text && (
            <span className="mt-2 inline-flex items-center gap-1 text-[10px] text-pink-700 italic">
              <Heart size={10} className="fill-pink-500 text-pink-500" />
              Recordado: "{msg.loved_text}"
            </span>
          )}
          {(onDidntWork || onLoved) && msg.ops_count > 0 && !msg.retried && !msg.loved_text && (
            <div className="mt-2 flex flex-wrap gap-2">
              {onLoved && (
                <button
                  onClick={() => onLoved(msg)}
                  data-testid="loved-btn"
                  className="inline-flex items-center gap-1.5 text-[11px] font-bold text-pink-800 bg-pink-100 hover:bg-pink-200 border border-pink-300 px-2.5 py-1 rounded-lg transition-colors"
                  title="Que la IA recuerde este estilo en futuros cambios"
                >
                  <Heart size={11} strokeWidth={2.5} />
                  Me encantó · usá este estilo
                </button>
              )}
              {onDidntWork && (
                <button
                  onClick={() => onDidntWork(msg)}
                  data-testid="didnt-work-btn"
                  className="inline-flex items-center gap-1.5 text-[11px] font-bold text-amber-800 bg-amber-100 hover:bg-amber-200 border border-amber-300 px-2.5 py-1 rounded-lg transition-colors"
                  title="Deshace el cambio y le pide a la IA otra alternativa"
                >
                  <RotateCcw size={11} strokeWidth={2.5} />
                  No funcionó · probá de nuevo
                </button>
              )}
            </div>
          )}
          {msg.retried && (
            <span className="mt-2 inline-block text-[10px] text-gray-500 italic">Reintentado</span>
          )}
        </div>
      </div>
    );
  }

  if (msg.type === "checkpoint") {
    return (
      <div className="flex justify-center py-1">
        <div className="flex items-center gap-1.5 bg-green-50 border border-green-200 text-green-700 text-[11px] font-semibold px-3 py-1.5 rounded-full">
          <Flag size={10} /> {msg.content}
        </div>
      </div>
    );
  }

  if (msg.type === "accept_log") {
    return (
      <div className="flex items-start gap-2.5">
        <BotAvatar />
        <div className="bg-green-50 border border-green-200 rounded-2xl rounded-tl-sm px-4 py-3.5 max-w-[90%]">
          <div className="flex items-center gap-2 mb-2.5">
            <CheckCircle2 size={14} className="text-green-600" />
            <span className="text-xs font-black text-green-800">Modificación #{msg.checkpoint_num} confirmada</span>
          </div>
          <p className="text-xs text-gray-600 mb-2 leading-relaxed">{msg.instruction}</p>
          {msg.changes?.length > 0 && (
            <div className="space-y-1.5 bg-white rounded-xl p-3">
              <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wide mb-2">Cambios aplicados</p>
              {msg.changes.map((c, i) => (
                <div key={c.element ? `${c.element}-${i}` : i} className="flex items-start gap-2">
                  <Check size={10} className="text-green-500 mt-0.5 flex-shrink-0" />
                  <div className="text-xs text-gray-700">
                    <span className="font-semibold">{c.element || c}</span>
                    {c.action && <span className="text-gray-500"> — {c.action}</span>}
                    {c.detail && <div className="text-gray-400 text-[10px] mt-0.5">{c.detail}</div>}
                  </div>
                </div>
              ))}
            </div>
          )}
          <p className="text-[10px] text-gray-400 mt-2">
            {new Date(msg.timestamp).toLocaleString("es-UY", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
          </p>
        </div>
      </div>
    );
  }

  if (msg.type === "restore_log") {
    return (
      <div className="flex justify-center py-1">
        <div className="flex items-center gap-1.5 bg-amber-50 border border-amber-200 text-amber-700 text-[11px] font-semibold px-3 py-1.5 rounded-full">
          <RefreshCw size={10} /> Versión anterior restaurada
        </div>
      </div>
    );
  }

  if (msg.type === "inline_edit_hint") {
    const raw = msg.content || "";
    const parts = raw.split(/(\*\*[^*]+\*\*)/g);
    return (
      <div className="flex items-start gap-2.5" data-testid="inline-edit-hint-bubble">
        <BotAvatar />
        <div
          className="max-w-[92%] rounded-2xl rounded-tl-sm px-4 py-3"
          style={{
            background: "#FFFBEB",
            border: "1px solid #FDE68A",
          }}
        >
          <div className="flex items-center gap-1.5 mb-2">
            <span
              className="inline-flex items-center justify-center w-5 h-5 rounded-md"
              style={{ background: "#FEF3C7", color: "#B45309" }}
            >
              <svg width="11" height="11" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                <path d="M12 3a7 7 0 0 0-4 12.8V17a2 2 0 0 0 2 2h4a2 2 0 0 0 2-2v-1.2A7 7 0 0 0 12 3zM9 21h6" />
              </svg>
            </span>
            <span className="text-[10px] font-black uppercase tracking-wider text-[#B45309]">
              Tip · Editá más rápido
            </span>
            <span className="ml-auto text-[9px] font-bold text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded">
              GRATIS
            </span>
          </div>
          <p className="text-[13px] text-gray-800 leading-relaxed">
            {parts.map((p, i) =>
              p.startsWith("**") && p.endsWith("**")
                ? <span key={i} className="font-bold text-[#78350F]">{p.slice(2, -2)}</span>
                : <span key={i}>{p}</span>
            )}
          </p>
        </div>
      </div>
    );
  }

  if (msg.type === "session_summary") {
    const summaryText = (msg.content || "").replace(/^\[.*?\]\n/, "").trim();
    return (
      <div className="flex justify-center py-2">
        <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 max-w-[92%]">
          <div className="flex items-center gap-2 mb-2">
            <Flag size={11} className="text-blue-500" />
            <p className="text-[10px] font-black text-blue-600 uppercase tracking-wide">Resumen de sesión anterior</p>
          </div>
          <p className="text-xs text-gray-700 leading-relaxed">{summaryText || "Sesión anterior cerrada."}</p>
        </div>
      </div>
    );
  }

  if (msg.type === "proposal" && msg.status === "pending") {
    return (
      <div className="flex items-start gap-2.5">
        <BotAvatar />
        <div className="max-w-[88%] space-y-2">
          <div className="bg-white border border-gray-200 rounded-2xl rounded-tl-sm px-4 py-3.5">
            <p className="text-sm font-semibold text-gray-800 mb-3">{msg.content}</p>
            <div className="space-y-2">
              {(msg.changes || []).map((c, i) => (
                <div key={c.element ? `${c.element}-${i}` : i} className="flex items-start gap-2 bg-purple-50 rounded-lg px-3 py-2">
                  <div className="w-4 h-4 rounded-full bg-[#6D28D9] flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-[8px] font-black text-white">{i + 1}</span>
                  </div>
                  <div>
                    <p className="text-xs font-bold text-gray-800">{c.element || c}</p>
                    {c.action && <p className="text-[10px] text-gray-500">{c.action}</p>}
                    {c.detail && <p className="text-[10px] text-purple-700 font-medium">{c.detail}</p>}
                  </div>
                </div>
              ))}
            </div>

            {/* Preview loading indicator inside card */}
            {previewLoading && (
              <div className="mt-3 p-2.5 bg-amber-50 rounded-xl border border-amber-100">
                <div className="flex items-center gap-2 mb-1.5">
                  <Loader2 size={11} className="animate-spin text-amber-500 flex-shrink-0" />
                  <p className="text-[10px] font-bold text-amber-700">Generando vista previa en el panel derecho...</p>
                </div>
                <div className="w-full h-1 bg-amber-100 rounded-full overflow-hidden">
                  <div className="h-full bg-amber-400 rounded-full animate-[shimmer_2s_ease-in-out_infinite]"
                    style={{ width: "60%", animation: "pulse 1.5s ease-in-out infinite" }} />
                </div>
                <p className="text-[9px] text-amber-500 mt-1">Podés confirmar ahora o esperar para revisar primero</p>
              </div>
            )}
            {!previewLoading && !accepting && (
              <p className="text-[10px] text-gray-400 mt-3 flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-green-400 inline-block" />
                Vista previa lista — revisá el panel derecho antes de confirmar
              </p>
            )}
          </div>
          <div className="flex gap-2">
            <button onClick={() => onAccept(msg.pending_id, msg)} disabled={accepting}
              data-testid="accept-proposal-btn"
              className="flex-1 flex items-center justify-center gap-1.5 py-2.5 bg-[#6D28D9] text-white rounded-xl text-sm font-bold hover:bg-purple-800 disabled:opacity-60 ring-2 ring-purple-400 ring-offset-1 animate-pulse-soft"
              style={{ boxShadow: "0 0 0 0 rgba(109,40,217,0.5)", animation: accepting ? "none" : "sy-pulse-cta 2s ease-in-out infinite" }}>
              {accepting ? <Loader2 size={13} className="animate-spin" /> : <Zap size={13} />}
              {accepting
                ? acceptingTimer > 5
                  ? `Aplicando... ${Math.floor(acceptingTimer / 60)}:${String(acceptingTimer % 60).padStart(2, "0")}`
                  : "Aplicando..."
                : previewLoading
                ? "Confirmar cambio — 1 crédito"
                : "Aplicar cambios — 1 crédito"}
            </button>
            <button onClick={() => onContinue(msg.pending_id, prevInstruction)} disabled={accepting}
              data-testid="continue-modify-btn"
              className="flex items-center justify-center gap-1.5 py-2.5 px-3 bg-gray-100 text-gray-700 rounded-xl text-sm font-semibold hover:bg-gray-200 disabled:opacity-60">
              <Wand2 size={13} /> Refinar
            </button>
          </div>
          {/* Hint so users don't miss the pulse and end up confused about
              why "nothing applied" — a recurring support issue. */}
          {!accepting && (
            <p className="mt-2 text-[10.5px] text-purple-700 italic leading-snug">
              👆 Hacé click en <strong>"Aplicar cambios"</strong> para que la IA modifique tu sitio.
            </p>
          )}
          <style dangerouslySetInnerHTML={{ __html: `
            @keyframes sy-pulse-cta {
              0%, 100% { box-shadow: 0 0 0 0 rgba(109,40,217,0.55); }
              50%      { box-shadow: 0 0 0 8px rgba(109,40,217,0.0);  }
            }
          `}} />
        </div>
      </div>
    );
  }

  if (msg.type === "applied_proposal" && msg.status !== "confirmed") {
    return (
      <div className="flex items-start gap-2.5">
        <BotAvatar />
        <div className="max-w-[88%] space-y-2">
          <div className="bg-green-50 border border-green-200 rounded-2xl rounded-tl-sm px-4 py-3.5">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle2 size={14} className="text-green-600" />
              <p className="text-sm font-bold text-green-800">Cambios listos en tu sitio</p>
            </div>
            <p className="text-xs text-gray-600">Revisá tu sitio antes de confirmar. Tenés tres opciones:</p>
          </div>
          <div className="flex flex-col gap-1.5">
            <button onClick={() => onConfirm(msg)} disabled={accepting || restoring} data-testid="confirm-changes-btn"
              className="flex items-center justify-center gap-2 py-3 bg-green-600 text-white rounded-xl text-sm font-bold hover:bg-green-700 disabled:opacity-60">
              <CheckCircle2 size={14} /> Confirmar modificación
            </button>
            <button onClick={() => onRestoreFlow(msg)} disabled={accepting || restoring} data-testid="restore-version-btn"
              className="flex items-center justify-center gap-2 py-2.5 bg-amber-50 border border-amber-200 text-amber-800 rounded-xl text-sm font-semibold hover:bg-amber-100 disabled:opacity-60">
              {restoring ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
              Restaurar versión anterior
            </button>
            <button onClick={() => onContinue(msg.pending_id, msg.instruction || prevInstruction)} disabled={accepting || restoring} data-testid="continue-after-apply-btn"
              className="flex items-center justify-center gap-2 py-2.5 bg-gray-100 text-gray-700 rounded-xl text-sm font-semibold hover:bg-gray-200 disabled:opacity-60">
              <Wand2 size={13} /> Pedir otro cambio
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (msg.type === "applied_proposal" && msg.status === "confirmed") {
    return (
      <div className="flex justify-center py-1">
        <div className="flex items-center gap-1.5 bg-green-50 border border-green-200 text-green-700 text-[11px] px-3 py-1.5 rounded-full">
          <CheckCircle2 size={10} /> Cambio confirmado
        </div>
      </div>
    );
  }

  if (msg.type === "proposal" && msg.status === "rejected") {
    return (
      <div className="flex justify-center py-1">
        <div className="flex items-center gap-1.5 bg-gray-100 text-gray-500 text-[11px] px-3 py-1.5 rounded-full">
          <X size={10} /> Propuesta descartada
        </div>
      </div>
    );
  }

  if (msg.type === "proposal" && msg.status === "accepted") {
    return (
      <div className="flex justify-center py-1">
        <div className="flex items-center gap-1.5 bg-purple-50 border border-purple-200 text-purple-700 text-[11px] px-3 py-1.5 rounded-full">
          <Check size={10} /> Cambios aplicados al sitio
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-start gap-2.5">
      <BotAvatar />
      <div className="bg-white border border-gray-200 rounded-2xl rounded-tl-sm px-4 py-3 max-w-[85%]">
        <p className="text-sm text-gray-700 leading-relaxed">{msg.content}</p>
        {msg.timestamp && (
          <p className="text-[10px] text-gray-400 mt-1.5">
            {new Date(msg.timestamp).toLocaleTimeString("es-UY", { hour: "2-digit", minute: "2-digit" })}
          </p>
        )}
      </div>
    </div>
  );
}

// ─── User Bubble ──────────────────────────────────────────────────────────────
export function UserBubble({ msg, initials }) {
  return (
    <div className="flex items-start gap-2.5 flex-row-reverse">
      <div className="w-8 h-8 rounded-full bg-[#6D28D9] flex items-center justify-center flex-shrink-0 mt-0.5">
        <span className="text-white text-[11px] font-black">{initials}</span>
      </div>
      <div className="bg-[#6D28D9] text-white rounded-2xl rounded-tr-sm px-4 py-3 max-w-[80%]">
        <p className="text-sm leading-relaxed">{msg.content}</p>
        {msg.has_image && (
          <div className="flex items-center gap-1 mt-1.5 opacity-70">
            <Image size={10} />
            <span className="text-[10px]">Imagen adjunta</span>
          </div>
        )}
        {msg.timestamp && (
          <p className="text-[10px] text-purple-200 mt-1.5">
            {new Date(msg.timestamp).toLocaleTimeString("es-UY", { hour: "2-digit", minute: "2-digit" })}
          </p>
        )}
      </div>
    </div>
  );
}
